import { ResourcesService, Resources } from '../services/resources.service'
import TextureKeys from './textureKeys' 
import TweenHelper from './tweenHelper' 
import { GameOptions, GameConsts } from './gameOptions';
import DisplayHelper from './displayHelper';
import { ModernPlayButton } from './modernPlayButton';

// UI Configuration
const UI_CONFIG = {
    // Main play button (larger, centered)
    PLAY_BUTTON_SCALE: 0.2,
    PLAY_BUTTON_HOVER_SCALE: 0.22,
    PLAY_BUTTON_PRESS_SCALE: 0.18,
    
    // Secondary buttons (smaller, positioned around edges)
    SECONDARY_BUTTON_SCALE: 0.08,
    SECONDARY_BUTTON_HOVER_SCALE: 0.1,
    SECONDARY_BUTTON_PRESS_SCALE: 0.06,
    
    LOGO_SCALE: 0.25,
    CHARACTER_SCALE: { boy: 0.55, girl: 0.71, santa: 0.4 },
    ANIMATION_DURATION: 500,
    LOADING_BAR_WIDTH: 400,
    LOADING_BAR_HEIGHT: 50
};

interface UIElements {
    playButton?: Phaser.GameObjects.Image;
    raceModeButton?: Phaser.GameObjects.Image;
    helpButton?: Phaser.GameObjects.Image;
    loadingBar?: Phaser.GameObjects.Rectangle;
    loadingBarBg?: Phaser.GameObjects.Rectangle;
    loadingText?: Phaser.GameObjects.Text;
    characters?: { left?: Phaser.GameObjects.Image; right?: Phaser.GameObjects.Image };
    [key: string]: any;
}

let isFirst = true;
export class PreloadAssets extends Phaser.Scene {
    
    private loadFinished = false;
    private loadComplete = false;
    private instructionsText: Phaser.GameObjects.Text;
    private logo: Phaser.GameObjects.Image
    private rotate: Phaser.GameObjects.Image
    private instructions: Phaser.GameObjects.Image
    private uiElements: UIElements = {};
    
    // Button positions
    private startButtonPosW = 0;
    private startButtonPosH = 0;
    private modeButtonPosW = 0;
    private modeButtonPosH = 0;
    private helpButtonPosW = 0;
    private helpButtonPosH = 0;
    private muteButtonPosW = 0;
    private muteButtonPosH = 0;
    private nameInputPosW = 0;
    private nameInputPosH = 0;
    private instructionsPosW = 0;
    private instructionsPosH = 0;
    
    private resource;
    private nameInput:any;
    private openGammataPortal = false;
    private homeButtonX = 0;
    private homeButtonY = 0;
    
    // Audio
    private buttonHoverSound: Phaser.Sound.BaseSound;
    private buttonClickSound: Phaser.Sound.BaseSound;
    
    // For first time user
    private isFirstTimeUser = false;

    constructor(private resourcesService: ResourcesService) {
        super({
            key: 'PreloadAssets'
        });
    }

    init() {
        this.isFirstTimeUser = !localStorage.getItem('gammata_user_visited');
    }

    preload() {
        console.log('preload - before static images');
        
        // Create loading bar first
        this.createLoadingBar();
        
        this.load.html('form', 'assets/form.html');
        this.load.image('logo', 'assets/Gammatalogo1.png');
        this.load.image('bg-blue', 'assets/bg_starburst_blue_ipad@2x.png');
        this.load.image('bg-santa', 'assets/santa-background.jpg');
        this.load.image('background1', 'assets/background7.png');
        this.load.image('boy_opening', 'assets/boy_for_opening.png');
        this.load.image('girl_opening', 'assets/girl_for_opening.png');
        this.load.image('santa_opening', 'assets/santa_opening.png');
        this.load.image('rotatePhone', 'assets/rotetePhone.png');
        this.load.image(TextureKeys.HomeButton, 'assets/home.png');
        
        // Load button click sounds
        this.load.audio("buttonHover", ["assets/mixkit-game-bonus-reached-2065.wav"]);
        this.load.audio("buttonClick", ["assets/mixkit-quick-win-video-game-notification-269.wav"]);
        
        var self = this;
        this.load.once(Phaser.Loader.Events.COMPLETE, async () => {
            console.log('create start');
            const width = this.scale.width;
            const height = this.scale.height;
            
            // Don't hide loading bar yet - wait until all loading is complete
            
            // Modern button layout - centered play button, smaller secondary buttons in corners
            self.startButtonPosW = this.scale.width / 2;
            self.startButtonPosH = this.scale.height * 0.85;  // Near bottom, centered
            
            // Race mode button - top right corner
            self.modeButtonPosW = this.scale.width - 60;
            self.modeButtonPosH = 60;
            
            // Help button - next to race mode button
            self.helpButtonPosW = this.scale.width - 130;  // 70px to the left of race mode button
            self.helpButtonPosH = 60;
            
            // Mute button - upper left corner
            self.muteButtonPosW = 60;
            self.muteButtonPosH = 60;
            
            // Name input form - bottom right corner
            self.nameInputPosW = this.scale.width - 150;  // 150px from right edge
            self.nameInputPosH = this.scale.height - 100;  // 100px from bottom
            self.instructionsPosW = this.scale.width/2;
            self.instructionsPosH = this.scale.height/2 - 30;
            self.homeButtonX = this.scale.width * 0.5;
            self.homeButtonY = 280;
    
            // Use background1.png as the background image
            const backgroundImage = self.add.image(width/2, height/2, 'background1');
            backgroundImage.setDepth(-10);
            
            // Scale the background to cover the entire screen while maintaining aspect ratio
            const scaleX = width / backgroundImage.width;
            const scaleY = height / backgroundImage.height;
            const scale = Math.max(scaleX, scaleY); // Use max to ensure full coverage
            backgroundImage.setScale(scale);
            
            // Add subtle overlay pattern
            const overlay = self.add.graphics();
            overlay.setDepth(-5);
            overlay.setAlpha(0.1);
            
            // Create a dot pattern overlay
            for (let x = 0; x < width; x += 40) {
                for (let y = 0; y < height; y += 40) {
                    overlay.fillStyle(0xffffff);
                    overlay.fillCircle(x, y, 1);
                }
            }
    
            // Animate logo entrance
            self.logo = this.add.image(
                width/2,
                100,
                'logo'
            )
            .setOrigin(0.5, 0.5)
            .setScale(0)
            .setDepth(10);
            
            // Logo entrance animation
            self.tweens.add({
                targets: self.logo,
                scale: UI_CONFIG.LOGO_SCALE,
                duration: 600,
                ease: 'Back.out'
            });

            // Rotate phone indicator
            self.rotate = self.add.image(
                width * 0.85,
                height/2 - 200,
                'rotatePhone'
            )
            .setOrigin(0.5, 0.5)
            .setScale(0.3)
            .setVisible(false)
            .setDepth(30);

            if(window.innerHeight > window.innerWidth){
                self.rotate.setVisible(true);
                TweenHelper.flashElement(self, self.rotate, true, 'Linear', 800, 80);
            }

            window.addEventListener("orientationchange", function() {
                if(window.innerHeight < window.innerWidth) {
                    self.rotate.setVisible(true);
                }
                else {
                    self.rotate.setVisible(false);
                }  
              }, false);

            // Setup audio
            self.setupAudio();

            console.log('preload - finish initial load');

            await self.createPriv();
        });
        
        this.load.start();
    }

    private createLoadingBar() {
        const centerX = this.scale.width / 2;
        const centerY = this.scale.height / 2;
        
        // Create a dark background overlay for the loading screen to make logo more visible
        this.uiElements.loadingBackground = this.add.rectangle(
            centerX, centerY,
            this.scale.width, this.scale.height,
            0x000000, 0.8  // Dark background with 80% opacity
        );
        this.uiElements.loadingBackground.setDepth(-5);  // Behind loading elements but above background image
        
        // Loading bar background
        this.uiElements.loadingBarBg = this.add.rectangle(
            centerX, centerY, 
            UI_CONFIG.LOADING_BAR_WIDTH, 
            UI_CONFIG.LOADING_BAR_HEIGHT, 
            0x222222
        );
        this.uiElements.loadingBarBg.setStrokeStyle(3, 0xffffff);
        
        // Loading bar fill
        this.uiElements.loadingBar = this.add.rectangle(
            centerX - UI_CONFIG.LOADING_BAR_WIDTH / 2 + 5, 
            centerY, 
            0, 
            UI_CONFIG.LOADING_BAR_HEIGHT - 10, 
            0x00ff00
        );
        this.uiElements.loadingBar.setOrigin(0, 0.5);
        
        // Loading text
        this.uiElements.loadingText = this.add.text(
            centerX, 
            centerY - 40, 
            'Loading: 0%', 
            {
                fontSize: '28px',
                fontFamily: 'Luckiest Guy',
                color: '#ffffff',
                stroke: '#000000',
                strokeThickness: 4
            }
        ).setOrigin(0.5);
        
        // Start gradual progress animation immediately
        this.startGradualProgress();
        
        // Update progress based on actual loading (but don't let it go backwards)
        this.load.on('progress', (value: number) => {
            // Only update if actual progress is ahead of our gradual progress
            const currentProgress = this.getCurrentProgress();
            if (value > currentProgress) {
                this.updateProgressDisplay(value);
            }
        });
    }

    private currentProgressValue = 0;
    private progressTween: Phaser.Tweens.Tween | null = null;

    private startGradualProgress() {
        // Gradually advance progress over 4 seconds to about 80%
        this.progressTween = this.tweens.add({
            targets: { progress: 0 },
            progress: 0.8,
            duration: 4000,
            ease: 'Power2.out',
            onUpdate: (tween) => {
                const progress = tween.getValue();
                this.currentProgressValue = progress;
                this.updateProgressDisplay(progress);
            }
        });
    }

    private getCurrentProgress(): number {
        return this.currentProgressValue;
    }

    private updateProgressDisplay(value: number) {
        // Check if elements still exist before updating
        if (this.uiElements.loadingBar && this.uiElements.loadingBar.scene) {
            this.uiElements.loadingBar.width = (UI_CONFIG.LOADING_BAR_WIDTH - 10) * value;
        }
        if (this.uiElements.loadingText && this.uiElements.loadingText.scene) {
            this.uiElements.loadingText.setText(`Loading: ${Math.round(value * 100)}%`);
        }
        this.currentProgressValue = value;
    }

    private hideLoadingBar() {
        // Remove progress listener to prevent errors
        this.load.off('progress');
        
        this.tweens.add({
            targets: [this.uiElements.loadingBar, this.uiElements.loadingBarBg, this.uiElements.loadingText, this.uiElements.loadingBackground],
            alpha: 0,
            duration: 300,
            onComplete: () => {
                this.uiElements.loadingBar?.destroy();
                this.uiElements.loadingBarBg?.destroy();
                this.uiElements.loadingText?.destroy();
                this.uiElements.loadingBackground?.destroy();
                // Clear references
                this.uiElements.loadingBar = null;
                this.uiElements.loadingBarBg = null;
                this.uiElements.loadingText = null;
                this.uiElements.loadingBackground = null;
            }
        });
        
        // Also fade out the Gammata Studios logo to prevent overlap with game content
        if (this.logo) {
            this.tweens.add({
                targets: this.logo,
                alpha: 0,
                duration: 500,
                delay: 200, // Small delay after loading bar starts fading
                onComplete: () => {
                    this.logo?.destroy();
                    this.logo = null;
                }
            });
        }
    }

    private setupAudio() {
        // Button sounds
        if (this.sound.get('buttonHover')) {
            this.buttonHoverSound = this.sound.add('buttonHover', { volume: 0.2 });
        }
        if (this.sound.get('buttonClick')) {
            this.buttonClickSound = this.sound.add('buttonClick', { volume: 0.4 });
        }
    }

    continueLoad() {
        this.load.image(TextureKeys.Background, 'assets/bg_repeat_340x640.png');
        this.load.image(TextureKeys.MouseHole, 'assets/object_mousehole.png');
        this.load.image(TextureKeys.Window1, 'assets/object_window1.png');
        this.load.image(TextureKeys.Window2, 'assets/object_window2.png');
        this.load.image(TextureKeys.PlayButton, 'assets/play.png');
        this.load.image(TextureKeys.RaceModeButton, 'assets/round.png');
        this.load.image(TextureKeys.HelpButton, 'assets/help.png');
        this.load.image('instructions', 'assets/1800px-by-1536px.png');
        this.load.image('downarrow', 'assets/downarrow.png');
        this.load.image(TextureKeys.Bookcase1, 'assets/green_three_draw.png');
        this.load.image(TextureKeys.Bookcase2, 'assets/blue_3_shelves.png');
        this.load.image(TextureKeys.Coin, 'assets/object_coin.png');
        this.load.image(TextureKeys.Lego2, 'assets/lego_2.png');
        this.load.image(TextureKeys.Table, 'assets/light_wood_table.png');
        this.load.image(TextureKeys.Cactus, 'assets/house_plant_example_03.png');
        this.load.image(TextureKeys.Book, 'assets/blue_perspective_down.png');
        this.load.image(TextureKeys.ParticleStar, 'assets/star_01.png');
        this.load.image(TextureKeys.PhotoFrame, 'assets/Frame_charcoalBG.png');
        this.load.image(TextureKeys.Monitor, 'assets/computer_monitor.png');
        this.load.image(TextureKeys.MonitorDesk, 'assets/yellow_desk.png');
        this.load.image(TextureKeys.SNOW, 'assets/snow1.png');
        
        this.load.audio("flap", ["assets/mixkit-golf-shot-breaking-the-air-2124.wav"]);
        this.load.audio("hit", ["assets/mixkit-impact-of-a-strong-punch-2155.mp3"]);
        this.load.audio("score", ["assets/mixkit-quick-win-video-game-notification-269.wav"]);
        this.load.audio("gamerover", ["assets/gameover.mp3"]);
        
        this.load.atlas(
                        'coins',
                        'assets/coins-spritesheet.png',
                        'assets/coins-spritesheet.json');     

        this.loadCharacterImages(this.resource.chosenCharacter);
        
        var gamePics = this.resource.blobs;
        var customImagesList = [];

        gamePics.forEach((element, index) => { 
            var picName = 'pic'+(index).toString();
            if (element.toString().indexOf('startupimage.png') >= 0)
            {
                this.load.image('startupimage', element);   
            }
            else if (element.toString().indexOf('endimage.png') >= 0)
            {
                this.load.image('endimage', element);   
            } 
            else if (element.toString().indexOf('boy') >= 0)
            {

            }
            else if (element.toString().indexOf('girl') >= 0)
            {

            }
            else if (element.toString().indexOf('santa') >= 0)
            {

            }
            else
            {
                this.load.image(picName, element);
                customImagesList.push(picName);   
            }
            console.log('loding pic'+(index).toString() + ' ' + element);
        });

        var self = this;
        this.load.once(Phaser.Loader.Events.COMPLETE, () => {
            this.loadCharacterAnimation(this.resource.chosenCharacter);

            this.anims.create({
                key: 'coins', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'coins', frame: 'gold_coin_round_star_1@2x.png' },
                    { key: 'coins', frame: 'gold_coin_round_star_2@2x.png' },
                    { key: 'coins', frame: 'gold_coin_round_star_3@2x.png' },
                    { key: 'coins', frame: 'gold_coin_round_star_4@2x.png' },
                    { key: 'coins', frame: 'gold_coin_round_star_5@2x.png' },
                    { key: 'coins', frame: 'gold_coin_round_star_6@2x.png' },
                ],
                frameRate: 15,
                repeat: -1 // -1 to loop forever
            });
        
            self.loadComplete = true;
        });
        this.load.start();

        this.resourcesService.setCustomImagesList(customImagesList);
        this.resourcesService.setGameName(this.resource.gameName);
        this.resourcesService.setGemsText(this.resource.gemsText);
        this.resourcesService.setScoreMessageText(this.resource.scoreMessageText);
        this.resourcesService.setGameEndText(this.resource.gameEndText);
        this.resourcesService.setChosenCharacter(this.resource.chosenCharacter);
        this.resourcesService.setGameRecord(this.resource.gameRecord);
      
        this.loadFinished = true;
        console.log('continueLoad - end');     
    }

async createPriv() {
        console.log('create start');

        var gameId = this.resourcesService.getQueryString('gameid');

        if (!gameId) {

            this.add.text(this.homeButtonX,this.homeButtonY - 150, "No game ID found in URL", {
                fontFamily: 'Luckiest Guy',
                fontSize: '48px',
                color: GameConsts.whiteColor,
                fontStyle: 'normal',
                strokeThickness: 9,
    stroke: GameConsts.blackColor,
                shadow: { offsetY: 1, offsetX: 1, blur: 2, fill: true, color: GameConsts.blackColor, stroke: true }
            })
            .setOrigin(0.5, 0.5);

            this.add.text(this.homeButtonX,this.homeButtonY - 90, "To create your own custom game,\nvisit setup.gammata.net", {
                fontFamily: 'Luckiest Guy',
                fontSize: '44px',
                color: GameConsts.whiteColor,
                fontStyle: 'normal',
                strokeThickness: 9,
                stroke: GameConsts.blackColor,
                shadow: { offsetY: 1, offsetX: 1, blur: 2, fill: true, color: GameConsts.blackColor, stroke: true },
                wordWrap: { width: this.scale.width - 80 },
                align: 'center'
            })
            .setOrigin(0.5, 0.5);

            // Add clickable link button
            const linkButton = this.add.text(this.homeButtonX, this.homeButtonY, "Click here to go to setup.gammata.net", {
                fontFamily: 'Luckiest Guy',
                fontSize: '36px',
                color: '#00ff00',
                fontStyle: 'normal',
                strokeThickness: 6,
                stroke: GameConsts.blackColor,
                shadow: { offsetY: 1, offsetX: 1, blur: 2, fill: true, color: GameConsts.blackColor, stroke: true }
            })
            .setOrigin(0.5, 0.5)
            .setInteractive()
            .setDepth(100);

            // Add hover effect
            linkButton.on('pointerover', () => {
                linkButton.setScale(1.1);
                linkButton.setColor('#ffff00');
            });

            linkButton.on('pointerout', () => {
                linkButton.setScale(1);
                linkButton.setColor('#00ff00');
            });

            linkButton.on('pointerdown', () => {
                window.location.assign('https://setup.gammata.net');
            });

            // Add pulsing animation to make the link more noticeable
            this.tweens.add({
                targets: linkButton,
                scale: 1.05,
                duration: 1000,
                ease: 'Sine.inOut',
                yoyo: true,
                repeat: -1
            });

            this.openGammataPortal = true;
            this.rotate.setVisible(false);

            this.sleep(200).then(() => {
                this.checkIfToNavigateToPortal();
            });
        }

        if (gameId) {
            this.resource = await this.resourcesService.getResources(this.resourcesService.getQueryString('gameid'));

            // xxx
            // this.resource.chosenCharacter = 'santa';
            
            if (this.resourcesService.getQueryString('deleteLocalVars') == 'true')
            {
                localStorage.setItem('gammata player name', '');
                localStorage.setItem('raceModeTried', '');
            }
    
            this.continueLoad();
    
            const width = this.scale.width;
            const height = this.scale.height;
    
            this.sleep(200).then(() => {
                this.checkIfToStart();        
            });
    
            console.log('create end');    
        }        
}

    checkIfToNavigateToPortal() {
        if (this.openGammataPortal) {
            console.log('portal');
            if (this.input.activePointer.isDown)
            {
                console.log('portal - down');
                if ((Phaser.Math.Difference(this.input.activePointer.downX,  this.homeButtonX) < 50 &&  Phaser.Math.Difference(this.input.activePointer.downY, this.homeButtonY) < 50))
                {
                    this.openGammataPortal = false;
                    window.location.assign('https://setup.gammata.net')
                }
            } else {
                this.sleep(200).then(() => {
                    this.checkIfToNavigateToPortal();
                });
            }
        }   
    }

    checkIfToStart() {
        if (this.loadFinished && this.loadComplete)
        { 
            if (isFirst) {
                isFirst = false;
                this.addCustomImage();
                this.takeSnapshot();
                
                console.log('image added');
            }   
                
            if ((Phaser.Math.Difference(this.input.activePointer.downX, this.startButtonPosW ) < 100 &&  Phaser.Math.Difference(this.input.activePointer.downY, this.startButtonPosH) < 100))
            {
                // Fade out transition
                this.fadeOutAndStart();
            }  
            else
            {
                this.sleep(200).then(() => {
                    this.checkIfToStart();
                });        
            }  
        }
        else
        {
            this.sleep(200).then(() => {
                this.checkIfToStart();
            });
        }
    }

    private fadeOutAndStart() {
        this.tweens.add({
            targets: this.children.list,
            alpha: 0,
            duration: 500,
            onComplete: () => {
                console.log('image added - stoping scene');
                this.scene.stop('PreloadAssets');
                console.log('image added - starting new scene');
                this.scene.start('PlayGame');
            }
        });
    }

    addCustomImage()
    {
        const width = this.scale.width;
        const height = this.scale.height;

        // Hide loading bar now that everything is ready
        this.hideLoadingBar();

        if (this.resource.chosenCharacter == 'santa') {

            this.add.image(
                width/2,
                height/2,
                'bg-santa'
            )
            .setOrigin(0.5, 0.5)
            .setScale(1.4)
            .setDepth(-1);

            var animationForStart = 'santa-run';    
            
              const leftChar = this.add.sprite(
                110,
                height*0.8,
                animationForStart
            ).setOrigin(0.5, 10)
            .setScale(0)
            .setDepth(5);
            
            leftChar.play(animationForStart);
            
            
            // Animate characters entrance
            this.tweens.add({
                targets: leftChar,
                scale: 0.4,
                duration: 500,
                ease: 'Back.out',
                delay: 200
            });
            
            this.emitSnow();
        } else {
            if (this.resource.chosenCharacter === 'boy')
            {
                var animationForStart = 'boy-run';                
            }
            else if (this.resource.chosenCharacter === 'girl')
            {
                var animationForStart = 'girl-run';                
            }

            // Create left character as animated sprite using actual game character
            const leftChar = this.add.sprite(
                110,
                height*0.8,
                animationForStart
            ).setOrigin(0.5, 10)
            .setScale(0)
            .setDepth(5);
            
            leftChar.play(animationForStart);
    
            
            // Animate characters entrance
            this.tweens.add({
                targets: leftChar,
                scale: 0.55,
                duration: 500,
                ease: 'Back.out',
                delay: 200
            });            
        }

        const startTextX = width * 0.5;
        const startTextY = height * 0.65 - 50;

        var startText = this.add.text(startTextX, startTextY, this.resourcesService.getGameName(), {
            fontFamily: 'Luckiest Guy',
            fontSize: '58px',
            color: GameConsts.yellowColor,
            fontStyle: 'normal',
            stroke: GameConsts.blackColor,
            strokeThickness: 9,
            shadow: { offsetY: 1, offsetX: 1, blur: 2, fill: true, color: GameConsts.blackColor, stroke: true },
            wordWrap: { width: this.scale.width - 100 },
            align: 'center'
        })
        .setOrigin(0.5)
        .setDepth(50);  // Set to very high depth to ensure it's always in front

        console.log('pic for start screen '+ this.resourcesService.getCustomImagesList()[0]);
        var currPic = this.add.image(
            width * 0.5,
            height * 0.45,
            'startupimage'
        ).setOrigin(0.5, 1);

        var scale =  height* 0.43/currPic.height;
        currPic.setScale(scale);

        // Create beautiful ornate frame around the startup image BEFORE enhancing the image
        this.createOrnateImageFrame(currPic);

        // Apply beautiful visual effects to enhance the image
        this.enhanceImageAppearance(currPic);

        // Add magical particles around the startup image
        this.createMagicalParticlesAroundImage(currPic);

        currPic.setDepth(8);

        // Show onboarding for first time users
        if (this.isFirstTimeUser) {
            this.time.delayedCall(1000, () => {
                this.showOnboarding();
            });
        }
    }

    private showOnboarding() {
        // Create overlay
        const overlay = this.add.rectangle(
            this.scale.width / 2,
            this.scale.height / 2,
            this.scale.width,
            this.scale.height,
            0x000000,
            0.7
        );
        overlay.setDepth(100);
        overlay.setInteractive(); // Block clicks
        
        // Onboarding panel
        const panel = this.add.rectangle(
            this.scale.width / 2,
            this.scale.height / 2,
            600,
            400,
            0x222222
        );
        panel.setStrokeStyle(3, 0xffffff);
        panel.setDepth(101);
        
        // Welcome text
        const welcomeText = this.add.text(
            this.scale.width / 2,
            this.scale.height / 2 - 150,
            'Welcome to ' + this.resourcesService.getGameName() + '!',
            {
                fontSize: '40px',
                fontFamily: 'Luckiest Guy',
                color: '#ffff00',
                stroke: '#000000',
                strokeThickness: 6,
                wordWrap: { width: 550 }
            }
        );
        welcomeText.setOrigin(0.5);
        welcomeText.setDepth(102);
        
        // Instructions
        const instructionsText = [
            '🏃 Run and jump to collect coins',
            '🏁 Try Race Mode for a challenge',
            '🎮 Click anywhere to jump',
            '🏆 Beat your high score!'
        ];
        
        const instructionObjects: Phaser.GameObjects.Text[] = [];
        instructionsText.forEach((text, index) => {
            const instruction = this.add.text(
                this.scale.width / 2,
                this.scale.height / 2 - 50 + (index * 40),
                text,
                {
                    fontSize: '24px',
                    fontFamily: 'Arial',
                    color: '#ffffff'
                }
            );
            instruction.setOrigin(0.5);
            instruction.setDepth(102);
            instructionObjects.push(instruction);
        });
        
        // Got it button
        const gotItButton = this.add.text(
            this.scale.width / 2,
            this.scale.height / 2 + 120,
            'Got it!',
            {
                fontSize: '32px',
                fontFamily: 'Luckiest Guy',
                color: '#00ff00',
                stroke: '#000000',
                strokeThickness: 4,
                padding: { left: 20, right: 20, top: 10, bottom: 10 }
            }
        );
        gotItButton.setOrigin(0.5);
        gotItButton.setDepth(102);
        gotItButton.setInteractive();
        
        // Add hover effect
        gotItButton.on('pointerover', () => {
            gotItButton.setScale(1.1);
            if (this.buttonHoverSound) this.buttonHoverSound.play();
        });
        
        gotItButton.on('pointerout', () => {
            gotItButton.setScale(1);
        });
        
        gotItButton.on('pointerdown', () => {
            localStorage.setItem('gammata_user_visited', 'true');
            if (this.buttonClickSound) this.buttonClickSound.play();
            
            this.tweens.add({
                targets: [overlay, panel, welcomeText, ...instructionObjects, gotItButton],
                alpha: 0,
                duration: 300,
                onComplete: () => {
                    overlay.destroy();
                    panel.destroy();
                    welcomeText.destroy();
                    instructionObjects.forEach(i => i.destroy());
                    gotItButton.destroy();
                }
            });
        });
    }

    // sleep time expects milliseconds
    sleep (time) {
        return new Promise((resolve) => setTimeout(resolve, time));
    }

    private takeSnapshot ()
    {
        var self = this;
        
        this.game.renderer.snapshot(function (image) {          
            // console.log('img = ' + (image as HTMLImageElement).currentSrc);
            console.log('snapshot taken');
            self.resourcesService.setSnapshot((image as HTMLImageElement).currentSrc);

            // Create modern animated play button
            const modernButton = new ModernPlayButton(self, self.startButtonPosW, self.startButtonPosH);
            
            // Add sound effects to the button
            modernButton.on('pointerover', () => {
                if (self.buttonHoverSound) self.buttonHoverSound.play();
            });
            
            modernButton.on('pointerdown', () => {
                if (self.buttonClickSound) self.buttonClickSound.play();
            });

            // Race Mode Button - smaller text for corner position
            const raceButtonText1X = self.modeButtonPosW;
            const raceButtonText1Y = self.modeButtonPosH - 8;
            const raceButtonText2X = self.modeButtonPosW;
            const raceButtonText2Y = self.modeButtonPosH + 8;

            var raceButtonText1 = self.add.text(raceButtonText1X, raceButtonText1Y, 'Race', {
                fontSize: '16px',
                color: GameConsts.blackColor,
                fontStyle: 'normal',
                stroke: GameConsts.blackColor,
                strokeThickness: 1,
                shadow: { offsetY: 1, offsetX: 1, blur: 2, fill: true, color: GameConsts.blackColor, stroke: true }
            })
            .setOrigin(0.5, 0.5)
            .setDepth(10);

            var raceButtonText2 = self.add.text(raceButtonText2X, raceButtonText2Y, 'mode', {
                fontSize: '16px',
                color: GameConsts.blackColor,
                fontStyle: 'normal',
                stroke: GameConsts.blackColor,
                strokeThickness: 1,
                shadow: { offsetY: 1, offsetX: 1, blur: 2, fill: true, color: GameConsts.blackColor, stroke: true }
            })
            .setOrigin(0.5, 0.5)
            .setDepth(10);

            const raceButtonX = self.modeButtonPosW;
            const raceButtonY = self.modeButtonPosH;

            const raceButton = self.add.sprite(raceButtonX, raceButtonY, TextureKeys.RaceModeButton)
            .setInteractive()
            .setOrigin(0.5, 0.5)
            .setScale(0);
            
            // Animate race button entrance
            self.tweens.add({
                targets: raceButton,
                scale: UI_CONFIG.SECONDARY_BUTTON_SCALE,
                duration: 400,
                ease: 'Back.out',
                delay: 500
            });
            
            // Add hover effects to race button
            raceButton.on('pointerover', () => {
                self.tweens.add({
                    targets: raceButton,
                    scale: UI_CONFIG.SECONDARY_BUTTON_HOVER_SCALE,
                    duration: 100
                });
                if (self.buttonHoverSound) self.buttonHoverSound.play();
            });
            
            raceButton.on('pointerout', () => {
                self.tweens.add({
                    targets: raceButton,
                    scale: UI_CONFIG.SECONDARY_BUTTON_SCALE,
                    duration: 100
                });
            });
            
            raceButton.on('pointerdown', () => {
                if (self.buttonClickSound) self.buttonClickSound.play();
                if (self.resourcesService.getGameMode() == 'RaceMode') { 
                    DisplayHelper.toggleButtonColor(0xffff, 'black', 'black', raceButton, raceButtonText1, raceButtonText2);
                    self.resourcesService.setGameMode('RegularMode');
                } else {
                    DisplayHelper.toggleButtonColor(0xFF0000, 'white', 'white', raceButton, raceButtonText1, raceButtonText2);
                    self.resourcesService.setGameMode('RaceMode');
                }
            });

            const arrow1X = self.modeButtonPosW;
            const arrow1Y = self.startButtonPosH - 83;
            const arrow2X = self.modeButtonPosW - 100;
            const arrow2Y = self.startButtonPosH - 83;
            const tryItText1X = self.modeButtonPosW;
            const tryItText1Y = self.startButtonPosH - 170;
            const tryItText2X = self.modeButtonPosW;
            const tryItText2Y = self.startButtonPosH - 130;

            if ((localStorage.getItem('gammata player name') == null || localStorage.getItem('gammata player name') == '')){
                self.nameInput = self.add.dom(self.nameInputPosW, self.nameInputPosH).createFromCache("form");
                self.nameInput.setScale(0);
                self.nameInput.setDepth(5);  // Reduced depth to be below the game name text
                
                // Animate name input entrance - delayed until after game name fades out
                self.tweens.add({
                    targets: self.nameInput,
                    scale: 1,
                    duration: 500,
                    ease: 'Back.out',
                    delay: 4500  // Delay until after game name text disappears (3000 delay + 1000 fade + 500 buffer)
                });
                
                // Add subtle floating animation - also delayed
                self.tweens.add({
                    targets: self.nameInput,
                    y: '+=10',
                    duration: 2500,
                    ease: 'Sine.inOut',
                    yoyo: true,
                    repeat: -1,
                    delay: 5000  // Start floating after entrance animation
                });
            }

            // Create animated help button
            const helpButton = self.add.sprite(self.helpButtonPosW, self.helpButtonPosH, TextureKeys.HelpButton)
            .setInteractive()
            .setOrigin(0.5, 0.5)
            .setScale(0);
            
            // Animate help button entrance
            self.tweens.add({
                targets: helpButton,
                scale: 0.08,
                duration: 400,
                ease: 'Back.out',
                delay: 600
            });
            
            // Add hover effects to help button
            helpButton.on('pointerover', () => {
                self.tweens.add({
                    targets: helpButton,
                    scale: 0.1,
                    duration: 100
                });
                if (self.buttonHoverSound) self.buttonHoverSound.play();
            });
            
            helpButton.on('pointerout', () => {
                self.tweens.add({
                    targets: helpButton,
                    scale: 0.08,
                    duration: 100
                });
            });
            
            helpButton.on('pointerdown', () => {
                if (self.buttonClickSound) self.buttonClickSound.play();
                if (self.instructions) {
                    self.instructions.destroy();
                    self.instructions = null;
                    self.instructionsText.destroy();
                    self.instructionsText = null;
                    if (self.nameInput) {
                        self.nameInput.setVisible(true);
                    }

                    helpButton.setTint(0xffff);
                } else {
                    if (self.nameInput) {
                        self.nameInput.setVisible(false);
                    }

                    helpButton.setTint(0xFFD700);

                    self.instructions = self.add.image(self.instructionsPosW, self.instructionsPosH, 'instructions')
                    .setScale(0.35)
                    .setOrigin(0.5, 0.5)
                    .setDepth(10);

                    self.instructionsText = self.add.text(self.instructionsPosW, self.instructionsPosH, 'In race mode you should run and jump over obstecales and finish as fast as you can a 400m race.\n\nYou will see another player image (shadow) which represents the best time you or another player of your custom game made.\n\nRace the shadow to become the new record holder!', {
                        fontSize: '30px',
                        color: GameConsts.blackColor,
                        fontStyle: 'normal',
                        stroke: GameConsts.blackColor,
                        strokeThickness: 1,
                        wordWrap: { width: 530 }
                    })
                    .setOrigin(0.5, 0.5)
                    .setDepth(10);
        
                }
            });
            
            // Create custom mute/unmute button using graphics
            const muteButton = self.createMuteButton(self.muteButtonPosW, self.muteButtonPosH);
        });
    }

    private loadCharacterImages (character: string)
    {
        if (character == 'girl')
        {
            const girlPng = this.resource.blobs.find(blob => blob.includes("girl.png"));
            const girlJson = this.resource.blobs.find(blob => blob.includes("girl.json"));
            this.load.atlas(
                'girl-anim',
                girlPng,
                girlJson);                             
        } else if (character == 'boy') {
            const boyPng = this.resource.blobs.find(blob => blob.includes("boy.png"));
            const boyJson = this.resource.blobs.find(blob => blob.includes("boy.json"));
            this.load.atlas(
                'boy-anim',
                boyPng,
                boyJson);
        } else if (character == 'santa') {
            const santaPng = this.resource.blobs.find(blob => blob.includes("santa.png"));
            const santaJson = this.resource.blobs.find(blob => blob.includes("santa.json"));
            this.load.atlas(
                'santa',
                santaPng,
                santaJson);


            // this.load.atlas(
            //                 'santa',
            //                 'assets/santa-spritesheet.png',
            //                 'assets/santa-spritesheet.json');
        }
    }

    private loadCharacterAnimation (character: string)
    {
        if (character == 'girl')
        {
            this.anims.create({
                key: 'girl-dizzy', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'girl-anim', frame: 'Dizzy_000.png' },
                    { key: 'girl-anim', frame: 'Dizzy_001.png' },
                    { key: 'girl-anim', frame: 'Dizzy_002.png' },
                ],
                frameRate: 10,
                repeat:-1 // -1 to loop forever
            });
    
            this.anims.create({
                key: 'girl-idle', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'girl-anim', frame: 'Idle_000.png' },
                    { key: 'girl-anim', frame: 'Idle_001.png' },
                    { key: 'girl-anim', frame: 'Idle_002.png' },
                    { key: 'girl-anim', frame: 'Idle_003.png' },
                    { key: 'girl-anim', frame: 'Idle_004.png' },
                    { key: 'girl-anim', frame: 'Idle_005.png' },
                    { key: 'girl-anim', frame: 'Idle_006.png' },
                    { key: 'girl-anim', frame: 'Idle_007.png' },
                    { key: 'girl-anim', frame: 'Idle_008.png' },
                    { key: 'girl-anim', frame: 'Idle_009.png' },
                    { key: 'girl-anim', frame: 'Idle_010.png' },
                    { key: 'girl-anim', frame: 'Idle_011.png' },
                ],
                frameRate: 10,
                repeat:-1 // -1 to loop forever
            });
    
            this.anims.create({
                key: 'girl-jump', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'girl-anim', frame: 'Jump_000.png' },
                    { key: 'girl-anim', frame: 'Jump_001.png' },
                    { key: 'girl-anim', frame: 'Jump_002.png' },
                    { key: 'girl-anim', frame: 'Jump_003.png' },
                    { key: 'girl-anim', frame: 'Jump_004.png' },
                    { key: 'girl-anim', frame: 'Jump_005.png' },
                    { key: 'girl-anim', frame: 'Jump_006.png' },
                    { key: 'girl-anim', frame: 'Jump_007.png' },
                ],
                frameRate: 6,
                repeat:-1 // -1 to loop forever
            });
    
            this.anims.create({
                key: 'girl-run', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'girl-anim', frame: 'Run_000.png' },
                    { key: 'girl-anim', frame: 'Run_001.png' },
                    { key: 'girl-anim', frame: 'Run_002.png' },
                    { key: 'girl-anim', frame: 'Run_003.png' },
                    { key: 'girl-anim', frame: 'Run_004.png' },
                    { key: 'girl-anim', frame: 'Run_005.png' },
                    { key: 'girl-anim', frame: 'Run_006.png' },
                    { key: 'girl-anim', frame: 'Run_007.png' },
                ],
                frameRate: 24,
                repeat:-1 // -1 to loop forever
            });    
        } else if (character == 'boy') {
            this.anims.create({
                key: 'boy-run', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'boy-anim', frame: 'Run_000.png' },
                    { key: 'boy-anim', frame: 'Run_001.png' },
                    { key: 'boy-anim', frame: 'Run_002.png' },
                    { key: 'boy-anim', frame: 'Run_003.png' },
                    { key: 'boy-anim', frame: 'Run_004.png' },
                    { key: 'boy-anim', frame: 'Run_005.png' },
                    { key: 'boy-anim', frame: 'Run_006.png' },
                    { key: 'boy-anim', frame: 'Run_007.png' },
                    // { key: 'boy-anim', frame: 'Run_008.png' },
                    // { key: 'boy-anim', frame: 'Run_009.png' },
                ],
                frameRate: 24,
                repeat: -1 // -1 to loop forever
            });
    
            this.anims.create({
                key: 'boy-jump', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'boy-anim', frame: 'Jump_000.png' },
                    { key: 'boy-anim', frame: 'Jump_001.png' },
                    { key: 'boy-anim', frame: 'Jump_002.png' },
                    { key: 'boy-anim', frame: 'Jump_003.png' },
                    { key: 'boy-anim', frame: 'Jump_004.png' },
                    { key: 'boy-anim', frame: 'Jump_005.png' },
                ],
                frameRate: 10,
                repeat: -1 // -1 to loop forever
            });
    
            this.anims.create({
                key: 'boy-idle', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'boy-anim', frame: 'Idle_000.png' },
                    { key: 'boy-anim', frame: 'Idle_001.png' },
                    { key: 'boy-anim', frame: 'Idle_002.png' },
                    { key: 'boy-anim', frame: 'Idle_003.png' },
                    { key: 'boy-anim', frame: 'Idle_004.png' },
                    { key: 'boy-anim', frame: 'Idle_005.png' },
                    { key: 'boy-anim', frame: 'Idle_006.png' },
                    { key: 'boy-anim', frame: 'Idle_007.png' },
                    { key: 'boy-anim', frame: 'Idle_008.png' },
                    { key: 'boy-anim', frame: 'Idle_009.png' },
                    { key: 'boy-anim', frame: 'Idle_010.png' },
                    { key: 'boy-anim', frame: 'Idle_011.png' },
                ],
                frameRate: 12,
                repeat: 0 // -1 to loop forever
            });
    
            this.anims.create({
                key: 'boy-dizzy', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'boy-anim', frame: 'Dizzy_000.png' },
                    { key: 'boy-anim', frame: 'Dizzy_001.png' },
                    { key: 'boy-anim', frame: 'Dizzy_002.png' },
                    { key: 'boy-anim', frame: 'Dizzy_003.png' },
                    //{ key: 'boy', frame: 'Die_002.png' },
                ],
                frameRate: 7,
                repeat: 0 // -1 to loop forever
            });    
        } else if (character == 'santa') {
            this.anims.create({
                key: 'santa-run', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'santa', frame: 'Run_000.png' },
                    { key: 'santa', frame: 'Run_001.png' },
                    { key: 'santa', frame: 'Run_002.png' },
                    { key: 'santa', frame: 'Run_003.png' },
                    { key: 'santa', frame: 'Run_004.png' },
                    { key: 'santa', frame: 'Run_005.png' },
                    { key: 'santa', frame: 'Run_006.png' },
                ],
                frameRate: 24,
                repeat: -1 // -1 to loop forever
            });
    
            this.anims.create({
                key: 'santa-jump', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'santa', frame: 'Jump_000.png' },
                    { key: 'santa', frame: 'Jump_001.png' },
                    { key: 'santa', frame: 'Jump_002.png' },
                    { key: 'santa', frame: 'Jump_003.png' },
                    { key: 'santa', frame: 'Jump_004.png' },
                ],
                frameRate: 10,
                repeat: -1 // -1 to loop forever
            });
    
            this.anims.create({
                key: 'santa-idle', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'santa', frame: 'Idle_000.png' },
                    { key: 'santa', frame: 'Idle_001.png' },
                    { key: 'santa', frame: 'Idle_002.png' },
                    { key: 'santa', frame: 'Idle_003.png' },
                    { key: 'santa', frame: 'Idle_004.png' },
                    { key: 'santa', frame: 'Idle_005.png' },
                    { key: 'santa', frame: 'Idle_006.png' },
                    // { key: 'santa', frame: 'Idle_007.png' },
                    // { key: 'santa', frame: 'Idle_008.png' },
                    // { key: 'santa', frame: 'Idle_009.png' },
                    // { key: 'santa', frame: 'Idle_010.png' },
                    // { key: 'santa', frame: 'Idle_011.png' },
                ],
                frameRate: 16,
                repeat: 0 // -1 to loop forever
            });
    
            this.anims.create({
                key: 'santa-dizzy', // name of this animation
                // helper to generate frames
                frames: [
                    { key: 'santa', frame: 'Dizzy_000.png' },
                    { key: 'santa', frame: 'Dizzy_001.png' },
                    { key: 'santa', frame: 'Dizzy_002.png' },
                    { key: 'santa', frame: 'Dizzy_003.png' },
                ],
                frameRate: 17,
                repeat: 0 // -1 to loop forever
            });    
        }
    }

    private createBackgroundParticles() {
        const width = this.scale.width;
        const height = this.scale.height;
        
        // Create floating particles for background ambiance
        for (let i = 0; i < 20; i++) {
            const particle = this.add.graphics();
            particle.setDepth(-8);
            particle.fillStyle(0xffffff, 0.3);
            particle.fillCircle(0, 0, Math.random() * 3 + 1);
            
            // Random position
            particle.x = Math.random() * width;
            particle.y = Math.random() * height;
            
            // Floating animation
            this.tweens.add({
                targets: particle,
                y: particle.y - 100,
                x: particle.x + (Math.random() - 0.5) * 50,
                alpha: 0,
                duration: Math.random() * 3000 + 2000,
                ease: 'Linear',
                repeat: -1,
                delay: Math.random() * 2000
            });
        }
    }

    private emitSnow() {
        const particles = this.add.particles(TextureKeys.SNOW);
        particles.setDepth(1000);
particles.createEmitter({
x: 0,
y: 0,
// emitZone
emitZone: {
source: new Phaser.Geom.Rectangle(-this.scale.width * 3, 0, this.scale.width * 7, 100),
type: 'random',
quantity: 30
},
speedY: { min: 50, max: 70 },
speedX: { min: -20, max: 20 },
accelerationY: { random: [10, 15] },
// lifespan
lifespan: { min: 8000, max: 10000 },
scale: { random: [0.025, 0.075] },
alpha: { random: [0.1, 0.8] },
gravityY: 10,
frequency: 10,
blendMode: 'ADD',
})
      }

    private createMuteButton(x: number, y: number): Phaser.GameObjects.Container {
        // Get current mute state from localStorage (default to unmuted)
        let isMuted = localStorage.getItem('gameAudioMuted') === 'true';
        
        // Create button container
        const container = this.add.container(x, y);
        container.setDepth(10);
        
        // Create stylish button background with gradient effect
        const bg = this.add.graphics();
        bg.fillGradientStyle(0x4a4a4a, 0x4a4a4a, 0x2a2a2a, 0x2a2a2a, 1);
        bg.fillCircle(0, 0, 35);
        bg.lineStyle(2, 0xffffff, 0.5);
        bg.strokeCircle(0, 0, 35);
        // Add inner glow
        bg.lineStyle(1, 0xffffff, 0.2);
        bg.strokeCircle(0, 0, 32);
        container.add(bg);
        
        // Create beautiful speaker icon
        const speaker = this.add.graphics();
        
        // Speaker body (cone shape) with gradient
        speaker.fillStyle(0xffffff, 1);
        speaker.beginPath();
        speaker.moveTo(-10, -8);
        speaker.lineTo(-10, 8);
        speaker.lineTo(-4, 5);
        speaker.lineTo(-4, -5);
        speaker.closePath();
        speaker.fillPath();
        
        // Speaker front (membrane)
        speaker.fillStyle(0xdddddd, 1);
        speaker.fillRect(-4, -5, 3, 10);
        
        // Speaker cone detail
        speaker.beginPath();
        speaker.moveTo(-4, -5);
        speaker.lineTo(4, -12);
        speaker.lineTo(4, 12);
        speaker.lineTo(-4, 5);
        speaker.closePath();
        speaker.fillPath();
        
        // Add speaker detail lines
        speaker.lineStyle(1, 0xaaaaaa, 0.5);
        speaker.lineBetween(-7, -4, -7, 4);
        speaker.lineBetween(-5, -3, -5, 3);
        
        // Sound waves (only show when not muted) - more detailed
        if (!isMuted) {
            // Multiple sound wave arcs for better effect
            speaker.lineStyle(2, 0xffffff, 0.8);
            speaker.beginPath();
            speaker.arc(6, 0, 8, -Math.PI/3, Math.PI/3, false);
            speaker.strokePath();
            
            speaker.lineStyle(2, 0xffffff, 0.6);
            speaker.beginPath();
            speaker.arc(6, 0, 14, -Math.PI/3.5, Math.PI/3.5, false);
            speaker.strokePath();
            
            speaker.lineStyle(2, 0xffffff, 0.4);
            speaker.beginPath();
            speaker.arc(6, 0, 20, -Math.PI/4, Math.PI/4, false);
            speaker.strokePath();
        }
        
        container.add(speaker);
        
        // Add mute X if muted - LARGER SIZE
        let muteX: Phaser.GameObjects.Graphics | null = null;
        if (isMuted) {
            muteX = this.add.graphics();
            muteX.lineStyle(4, 0xff0000);  // Thicker line
            muteX.lineBetween(-20, -20, 20, 20);  // Larger X
            muteX.lineBetween(-20, 20, 20, -20);
            container.add(muteX);
        }
        
        // Make container interactive - LARGER HIT AREA
        container.setSize(70, 70);  // Increased from 50 to 70
        container.setInteractive();
        
        // Set initial scale to 0 for entrance animation
        container.setScale(0);
        
        // Animate button entrance - LARGER SCALE
        this.tweens.add({
            targets: container,
            scale: 1,  // Full size for visibility
            duration: 400,
            ease: 'Back.out',
            delay: 700
        });
        
        // Add hover effects - LARGER SCALE
        container.on('pointerover', () => {
            this.tweens.add({
                targets: container,
                scale: 1.1,  // Subtle hover effect
                duration: 100
            });
            if (this.buttonHoverSound && !isMuted) this.buttonHoverSound.play();
        });
        
        container.on('pointerout', () => {
            this.tweens.add({
                targets: container,
                scale: 1,  // Back to normal
                duration: 100
            });
        });
        
        // Handle click to toggle mute
        container.on('pointerdown', () => {
            isMuted = !isMuted;
            
            // Save new state
            localStorage.setItem('gameAudioMuted', isMuted.toString());
            
            // Toggle audio
            if (isMuted) {
                // Mute all sounds
                this.sound.volume = 0;
            } else {
                // Unmute all sounds
                this.sound.volume = 1;
                // Play click sound to confirm unmuting
                if (this.buttonClickSound) this.buttonClickSound.play();
            }
            
            // Update button appearance - LARGER SIZE
            if (isMuted) {
                // Add mute X if not already present
                if (!muteX) {
                    muteX = this.add.graphics();
                    muteX.lineStyle(4, 0xff0000);
                    muteX.lineBetween(-20, -20, 20, 20);
                    muteX.lineBetween(-20, 20, 20, -20);
                    container.add(muteX);
                }
            } else {
                // Remove mute X if present
                if (muteX) {
                    muteX.destroy();
                    muteX = null;
                }
            }
            
            // Redraw speaker with updated state
            speaker.clear();
            
            // Speaker body (cone shape)
            speaker.fillStyle(0xffffff, 1);
            speaker.beginPath();
            speaker.moveTo(-10, -8);
            speaker.lineTo(-10, 8);
            speaker.lineTo(-4, 5);
            speaker.lineTo(-4, -5);
            speaker.closePath();
            speaker.fillPath();
            
            // Speaker front (membrane)
            speaker.fillStyle(0xdddddd, 1);
            speaker.fillRect(-4, -5, 3, 10);
            
            // Speaker cone detail
            speaker.beginPath();
            speaker.moveTo(-4, -5);
            speaker.lineTo(4, -12);
            speaker.lineTo(4, 12);
            speaker.lineTo(-4, 5);
            speaker.closePath();
            speaker.fillPath();
            
            // Add speaker detail lines
            speaker.lineStyle(1, 0xaaaaaa, 0.5);
            speaker.lineBetween(-7, -4, -7, 4);
            speaker.lineBetween(-5, -3, -5, 3);
            
            // Sound waves (only show when not muted)
            if (!isMuted) {
                // Multiple sound wave arcs
                speaker.lineStyle(2, 0xffffff, 0.8);
                speaker.beginPath();
                speaker.arc(6, 0, 8, -Math.PI/3, Math.PI/3, false);
                speaker.strokePath();
                
                speaker.lineStyle(2, 0xffffff, 0.6);
                speaker.beginPath();
                speaker.arc(6, 0, 14, -Math.PI/3.5, Math.PI/3.5, false);
                speaker.strokePath();
                
                speaker.lineStyle(2, 0xffffff, 0.4);
                speaker.beginPath();
                speaker.arc(6, 0, 20, -Math.PI/4, Math.PI/4, false);
                speaker.strokePath();
            }
        });
        
        // Apply initial mute state
        if (isMuted) {
            this.sound.volume = 0;
        }
        
        return container;
    }
    
    private updateMuteButtonAppearance(
        container: Phaser.GameObjects.Container, 
        speaker: Phaser.GameObjects.Graphics, 
        muteX: Phaser.GameObjects.Graphics | null, 
        isMuted: boolean
    ) {
        // Clear and redraw speaker
        speaker.clear();
        speaker.fillStyle(0xffffff);
        
        // Speaker base
        speaker.fillRect(-8, -6, 4, 12);
        speaker.fillTriangle(-4, -6, -4, 6, 2, 3);
        speaker.fillTriangle(-4, -6, -4, 6, 2, -3);
        
        // Sound waves (only show when not muted)
        if (!isMuted) {
            speaker.lineStyle(2, 0xffffff);
            speaker.arc(2, 0, 8, -Math.PI/3, Math.PI/3, false);
            speaker.arc(2, 0, 12, -Math.PI/4, Math.PI/4, false);
        }
        
        // Handle mute X
        if (isMuted && !muteX) {
            // Add mute X
            muteX = this.add.graphics();
            muteX.lineStyle(3, 0xff0000);
            muteX.lineBetween(-15, -15, 15, 15);
            muteX.lineBetween(-15, 15, 15, -15);
            container.add(muteX);
        } else if (!isMuted && muteX) {
            // Remove mute X
            muteX.destroy();
            muteX = null;
        }
    }

    private createBeautifulFrame(x: number, y: number, width: number, height: number): Phaser.GameObjects.Container {
        const frameContainer = this.add.container(x, y);
        
        // Main frame background with gradient effect
        const frameBackground = this.add.graphics();
        frameBackground.fillGradientStyle(0x8B4513, 0x8B4513, 0x654321, 0x654321, 1); // Brown wooden gradient
        frameBackground.fillRoundedRect(-width/2, -height/2, width, height, 12);
        frameContainer.add(frameBackground);
        
        // Inner frame (darker border)
        const innerFrame = this.add.graphics();
        innerFrame.lineStyle(6, 0x2F1B14, 1); // Dark brown border
        innerFrame.strokeRoundedRect(-width/2 + 3, -height/2 + 3, width - 6, height - 6, 8);
        frameContainer.add(innerFrame);
        
        // Decorative corners
        this.createFrameCorner(frameContainer, -width/2 + 15, -height/2 + 15, 'top-left');
        this.createFrameCorner(frameContainer, width/2 - 15, -height/2 + 15, 'top-right');
        this.createFrameCorner(frameContainer, -width/2 + 15, height/2 - 15, 'bottom-left');
        this.createFrameCorner(frameContainer, width/2 - 15, height/2 - 15, 'bottom-right');
        
        // Add subtle highlights for 3D effect
        const highlight = this.add.graphics();
        highlight.lineStyle(2, 0xD2B48C, 0.7); // Light brown highlight
        highlight.strokeRoundedRect(-width/2 + 1, -height/2 + 1, width - 2, height - 2, 10);
        frameContainer.add(highlight);
        
        // Add inner shadow effect
        const shadow = this.add.graphics();
        shadow.lineStyle(2, 0x1A1A1A, 0.3); // Dark shadow
        shadow.strokeRoundedRect(-width/2 + 8, -height/2 + 8, width - 16, height - 16, 6);
        frameContainer.add(shadow);
        
        // Note: Containers don't have setOrigin, positioning is handled by the container's x,y
        
        return frameContainer;
    }
    
    private createFrameCorner(container: Phaser.GameObjects.Container, x: number, y: number, position: string) {
        const corner = this.add.graphics();
        corner.fillStyle(0xFFD700, 0.8); // Golden decorative corner
        
        // Create a small decorative element
        corner.fillCircle(x, y, 4);
        corner.fillStyle(0xB8860B, 1); // Darker gold
        corner.fillCircle(x, y, 2);
        
        // Add small lines for detail
        corner.lineStyle(1, 0xFFD700, 1);
        corner.lineBetween(x - 6, y, x + 6, y);
        corner.lineBetween(x, y - 6, x, y + 6);
        
        container.add(corner);
    }

    // Method to enhance image appearance with visual effects
    private enhanceImageAppearance(image: Phaser.GameObjects.Image) {
        // Add a subtle drop shadow behind the image
        const shadow = this.add.graphics();
        shadow.setDepth(image.depth - 1);
        shadow.fillStyle(0x000000, 0.3);
        shadow.fillRoundedRect(
            image.x - image.displayWidth/2 + 8,
            image.y - image.displayHeight + 8,
            image.displayWidth,
            image.displayHeight,
            8
        );
        
        // Create a soft glow effect around the image
        const glow = this.add.graphics();
        glow.setDepth(image.depth - 0.5);
        
        // Create multiple glow layers for a soft effect
        for (let i = 0; i < 5; i++) {
            const glowSize = (i + 1) * 3;
            const alpha = 0.1 - (i * 0.015);
            glow.fillStyle(0xffffff, alpha);
            glow.fillRoundedRect(
                image.x - image.displayWidth/2 - glowSize,
                image.y - image.displayHeight - glowSize,
                image.displayWidth + (glowSize * 2),
                image.displayHeight + (glowSize * 2),
                12 + glowSize
            );
        }
        
        // Create faded edges effect using masks or overlays
        const fadeOverlay = this.add.graphics();
        fadeOverlay.setDepth(image.depth + 0.5);
        
        // Create a gradient fade effect on the edges
        const fadeWidth = 20; // How far the fade extends inward
        
        // Top fade
        fadeOverlay.fillGradientStyle(0x1e3c72, 0x1e3c72, 0x1e3c72, 0x1e3c72, 0.8, 0.8, 0, 0);
        fadeOverlay.fillRect(
            image.x - image.displayWidth/2,
            image.y - image.displayHeight,
            image.displayWidth,
            fadeWidth
        );
        
        // Bottom fade
        fadeOverlay.fillGradientStyle(0x1e3c72, 0x1e3c72, 0x1e3c72, 0x1e3c72, 0, 0, 0.8, 0.8);
        fadeOverlay.fillRect(
            image.x - image.displayWidth/2,
            image.y - fadeWidth,
            image.displayWidth,
            fadeWidth
        );
        
        // Left fade
        fadeOverlay.fillGradientStyle(0x1e3c72, 0x1e3c72, 0x1e3c72, 0x1e3c72, 0.8, 0, 0.8, 0);
        fadeOverlay.fillRect(
            image.x - image.displayWidth/2,
            image.y - image.displayHeight + fadeWidth,
            fadeWidth,
            image.displayHeight - (fadeWidth * 2)
        );
        
        // Right fade
        fadeOverlay.fillGradientStyle(0x1e3c72, 0x1e3c72, 0x1e3c72, 0x1e3c72, 0, 0.8, 0, 0.8);
        fadeOverlay.fillRect(
            image.x + image.displayWidth/2 - fadeWidth,
            image.y - image.displayHeight + fadeWidth,
            fadeWidth,
            image.displayHeight - (fadeWidth * 2)
        );
        
        // Add a subtle highlight on top edge of the image
        const highlight = this.add.graphics();
        highlight.setDepth(image.depth + 1);
        highlight.fillGradientStyle(0xffffff, 0xffffff, 0xffffff, 0xffffff, 0.2, 0.2, 0, 0);
        highlight.fillRect(
            image.x - image.displayWidth/2,
            image.y - image.displayHeight,
            image.displayWidth,
            8
        );
        
        // Add a gentle floating animation to make it feel more dynamic
        this.tweens.add({
            targets: [image, shadow, glow, fadeOverlay, highlight],
            y: '+=3',
            duration: 3000,
            ease: 'Sine.inOut',
            yoyo: true,
            repeat: -1
        });
    }

    // Method to create beautiful frames for in-game images as well
    createGameImageFrame(scene: Phaser.Scene, x: number, y: number, width: number, height: number): Phaser.GameObjects.Container {
        const frameContainer = scene.add.container(x, y);
        
        // Main frame background with metallic effect
        const frameBackground = scene.add.graphics();
        frameBackground.fillGradientStyle(0x708090, 0x708090, 0x2F4F4F, 0x2F4F4F, 1); // Metallic gradient
        frameBackground.fillRoundedRect(-width/2, -height/2, width, height, 8);
        frameContainer.add(frameBackground);
        
        // Outer border
        const outerBorder = scene.add.graphics();
        outerBorder.lineStyle(3, 0x000000, 0.8);
        outerBorder.strokeRoundedRect(-width/2, -height/2, width, height, 8);
        frameContainer.add(outerBorder);
        
        // Inner border with highlight
        const innerBorder = scene.add.graphics();
        innerBorder.lineStyle(2, 0xC0C0C0, 0.9); // Silver highlight
        innerBorder.strokeRoundedRect(-width/2 + 5, -height/2 + 5, width - 10, height - 10, 6);
        frameContainer.add(innerBorder);
        
        // Add corner decorations
        const corners = [
            {x: -width/2 + 8, y: -height/2 + 8},
            {x: width/2 - 8, y: -height/2 + 8},
            {x: -width/2 + 8, y: height/2 - 8},
            {x: width/2 - 8, y: height/2 - 8}
        ];
        
        corners.forEach(corner => {
            const decoration = scene.add.graphics();
            decoration.fillStyle(0xFFD700, 1);
            // Create diamond shape using beginPath
            decoration.beginPath();
            decoration.moveTo(corner.x - 3, corner.y);
            decoration.lineTo(corner.x, corner.y - 3);
            decoration.lineTo(corner.x + 3, corner.y);
            decoration.lineTo(corner.x, corner.y + 3);
            decoration.closePath();
            decoration.fillPath();
            frameContainer.add(decoration);
        });
        
        return frameContainer;
    }

    // Create an ornate decorative frame around the startup image
    private createOrnateImageFrame(image: Phaser.GameObjects.Image) {
        const frameWidth = image.displayWidth + 80;
        const frameHeight = image.displayHeight + 80;
        const centerX = image.x;
        const centerY = image.y - image.displayHeight/2;

        // Create main ornate frame container
        const frameContainer = this.add.container(centerX, centerY);
        frameContainer.setDepth(image.depth - 2);

        // Outer decorative border with gradient
        const outerFrame = this.add.graphics();
        outerFrame.fillGradientStyle(0xFFD700, 0xFFA500, 0xB8860B, 0xDAA520, 1); // Golden gradient
        outerFrame.fillRoundedRect(-frameWidth/2, -frameHeight/2, frameWidth, frameHeight, 20);
        frameContainer.add(outerFrame);

        // Inner frame border
        const innerFrame = this.add.graphics();
        innerFrame.fillGradientStyle(0x8B4513, 0x654321, 0x2F1B14, 0x3E2723, 1); // Dark wood gradient
        innerFrame.fillRoundedRect(-frameWidth/2 + 15, -frameHeight/2 + 15, frameWidth - 30, frameHeight - 30, 15);
        frameContainer.add(innerFrame);

        // Middle decorative border
        const middleFrame = this.add.graphics();
        middleFrame.lineStyle(8, 0xFFD700, 1);
        middleFrame.strokeRoundedRect(-frameWidth/2 + 25, -frameHeight/2 + 25, frameWidth - 50, frameHeight - 50, 12);
        frameContainer.add(middleFrame);

        // Inner content area
        const contentFrame = this.add.graphics();
        contentFrame.fillStyle(0x1a1a1a, 0.2); // Subtle dark background
        contentFrame.fillRoundedRect(-frameWidth/2 + 35, -frameHeight/2 + 35, frameWidth - 70, frameHeight - 70, 10);
        frameContainer.add(contentFrame);

        // Create ornate corner decorations
        const cornerPositions = [
            { x: -frameWidth/2 + 20, y: -frameHeight/2 + 20, rotation: 0 },
            { x: frameWidth/2 - 20, y: -frameHeight/2 + 20, rotation: Math.PI/2 },
            { x: frameWidth/2 - 20, y: frameHeight/2 - 20, rotation: Math.PI },
            { x: -frameWidth/2 + 20, y: frameHeight/2 - 20, rotation: -Math.PI/2 }
        ];

        cornerPositions.forEach(corner => {
            this.createOrnateCornerDecoration(frameContainer, corner.x, corner.y, corner.rotation);
        });

        // Create decorative flourishes on the sides
        this.createSideFlourishes(frameContainer, frameWidth, frameHeight);

        // Add a subtle pulsing glow effect to the frame
        this.tweens.add({
            targets: outerFrame,
            alpha: 0.8,
            duration: 2000,
            ease: 'Sine.inOut',
            yoyo: true,
            repeat: -1
        });

        // Animate frame entrance
        frameContainer.setScale(0);
        this.tweens.add({
            targets: frameContainer,
            scale: 1,
            duration: 800,
            ease: 'Back.out',
            delay: 100
        });
    }

    // Create ornate corner decorations
    private createOrnateCornerDecoration(container: Phaser.GameObjects.Container, x: number, y: number, rotation: number) {
        const cornerDecor = this.add.graphics();
        cornerDecor.setRotation(rotation);
        
        // Main decorative element - ornate swirl
        cornerDecor.fillStyle(0xFFD700, 1);
        cornerDecor.beginPath();
        cornerDecor.arc(x, y, 12, 0, Math.PI * 2);
        cornerDecor.fillPath();
        
        // Inner circle
        cornerDecor.fillStyle(0xB8860B, 1);
        cornerDecor.beginPath();
        cornerDecor.arc(x, y, 8, 0, Math.PI * 2);
        cornerDecor.fillPath();
        
        // Decorative spikes
        for (let i = 0; i < 8; i++) {
            const angle = (i * Math.PI * 2) / 8;
            const spikeX = x + Math.cos(angle) * 15;
            const spikeY = y + Math.sin(angle) * 15;
            const tipX = x + Math.cos(angle) * 20;
            const tipY = y + Math.sin(angle) * 20;
            
            cornerDecor.fillStyle(0xFFD700, 0.8);
            cornerDecor.fillTriangle(x, y, spikeX, spikeY, tipX, tipY);
        }
        
        container.add(cornerDecor);
    }

    // Create decorative flourishes on the sides
    private createSideFlourishes(container: Phaser.GameObjects.Container, frameWidth: number, frameHeight: number) {
        // Top flourish - decorative diamond pattern
        const topFlourish = this.add.graphics();
        topFlourish.fillStyle(0xFFD700, 0.9);
        topFlourish.fillTriangle(0, -frameHeight/2 + 5, -15, -frameHeight/2 + 15, 15, -frameHeight/2 + 15);
        topFlourish.fillTriangle(-10, -frameHeight/2 + 15, 0, -frameHeight/2 + 25, 10, -frameHeight/2 + 15);
        container.add(topFlourish);

        // Bottom flourish - decorative diamond pattern
        const bottomFlourish = this.add.graphics();
        bottomFlourish.fillStyle(0xFFD700, 0.9);
        bottomFlourish.fillTriangle(0, frameHeight/2 - 5, -15, frameHeight/2 - 15, 15, frameHeight/2 - 15);
        bottomFlourish.fillTriangle(-10, frameHeight/2 - 15, 0, frameHeight/2 - 25, 10, frameHeight/2 - 15);
        container.add(bottomFlourish);

        // Left flourish - decorative diamond pattern
        const leftFlourish = this.add.graphics();
        leftFlourish.fillStyle(0xFFD700, 0.9);
        leftFlourish.fillTriangle(-frameWidth/2 + 5, 0, -frameWidth/2 + 15, -15, -frameWidth/2 + 15, 15);
        leftFlourish.fillTriangle(-frameWidth/2 + 15, -10, -frameWidth/2 + 25, 0, -frameWidth/2 + 15, 10);
        container.add(leftFlourish);

        // Right flourish - decorative diamond pattern
        const rightFlourish = this.add.graphics();
        rightFlourish.fillStyle(0xFFD700, 0.9);
        rightFlourish.fillTriangle(frameWidth/2 - 5, 0, frameWidth/2 - 15, -15, frameWidth/2 - 15, 15);
        rightFlourish.fillTriangle(frameWidth/2 - 15, -10, frameWidth/2 - 25, 0, frameWidth/2 - 15, 10);
        container.add(rightFlourish);

        // Add small decorative gems at frame midpoints
        this.createDecorativeGems(container, frameWidth, frameHeight);
    }

    // Create small decorative gems
    private createDecorativeGems(container: Phaser.GameObjects.Container, frameWidth: number, frameHeight: number) {
        const gemPositions = [
            { x: 0, y: -frameHeight/2 - 5 }, // Top center
            { x: 0, y: frameHeight/2 + 5 },  // Bottom center
            { x: -frameWidth/2 - 5, y: 0 },  // Left center
            { x: frameWidth/2 + 5, y: 0 }    // Right center
        ];

        gemPositions.forEach((pos, index) => {
            const gem = this.add.graphics();
            const colors = [0xFF1493, 0x00FF7F, 0x1E90FF, 0xFF4500]; // Different colors for each gem
            gem.fillStyle(colors[index], 0.8);
            gem.fillCircle(pos.x, pos.y, 8);
            gem.lineStyle(2, 0xFFD700, 1);
            gem.strokeCircle(pos.x, pos.y, 8);
            
            // Add inner sparkle
            gem.fillStyle(0xFFFFFF, 0.9);
            gem.fillCircle(pos.x - 2, pos.y - 2, 2);
            
            container.add(gem);

            // Add gentle pulsing animation to gems
            this.tweens.add({
                targets: gem,
                scaleX: 1.3,
                scaleY: 1.3,
                duration: 1000 + (index * 200),
                ease: 'Sine.inOut',
                yoyo: true,
                repeat: -1
            });
        });
    }

    // Create magical particles around the startup image
    private createMagicalParticlesAroundImage(image: Phaser.GameObjects.Image) {
        const particleCount = 15;
        const particles: Phaser.GameObjects.Graphics[] = [];

        for (let i = 0; i < particleCount; i++) {
            const particle = this.add.graphics();
            particle.setDepth(image.depth + 1);
            
            // Create different types of magical particles
            const particleType = i % 3;
            switch (particleType) {
                case 0: // Stars
                    this.createStarParticle(particle, 0xFFD700);
                    break;
                case 1: // Diamonds
                    this.createDiamondParticle(particle, 0x00FFFF);
                    break;
                case 2: // Circles
                    this.createCircleParticle(particle, 0xFF69B4);
                    break;
            }

            // Position particles around the image
            const angle = (i / particleCount) * Math.PI * 2;
            const radius = Math.max(image.displayWidth, image.displayHeight) / 2 + 50;
            particle.x = image.x + Math.cos(angle) * radius;
            particle.y = image.y - image.displayHeight/2 + Math.sin(angle) * radius;

            particles.push(particle);

            // Animate particles orbiting around the image
            this.tweens.add({
                targets: particle,
                angle: '+=360',
                duration: 8000 + (i * 200), // Stagger the timing
                ease: 'Linear',
                repeat: -1
            });

            // Add floating motion
            this.tweens.add({
                targets: particle,
                y: particle.y + (Math.random() - 0.5) * 20,
                duration: 2000 + (i * 100),
                ease: 'Sine.inOut',
                yoyo: true,
                repeat: -1
            });

            // Add scale pulsing
            this.tweens.add({
                targets: particle,
                scaleX: 1.2,
                scaleY: 1.2,
                duration: 1500 + (i * 50),
                ease: 'Sine.inOut',
                yoyo: true,
                repeat: -1
            });
        }
    }

    // Create star-shaped particle
    private createStarParticle(graphics: Phaser.GameObjects.Graphics, color: number) {
        graphics.fillStyle(color, 0.8);
        graphics.beginPath();
        const points = 5;
        const outerRadius = 6;
        const innerRadius = 3;
        
        for (let i = 0; i < points * 2; i++) {
            const angle = (i * Math.PI) / points;
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            
            if (i === 0) {
                graphics.moveTo(x, y);
            } else {
                graphics.lineTo(x, y);
            }
        }
        graphics.closePath();
        graphics.fillPath();
    }

    // Create diamond-shaped particle
    private createDiamondParticle(graphics: Phaser.GameObjects.Graphics, color: number) {
        graphics.fillStyle(color, 0.7);
        graphics.beginPath();
        graphics.moveTo(0, -8);
        graphics.lineTo(5, 0);
        graphics.lineTo(0, 8);
        graphics.lineTo(-5, 0);
        graphics.closePath();
        graphics.fillPath();
    }

    // Create circle particle
    private createCircleParticle(graphics: Phaser.GameObjects.Graphics, color: number) {
        graphics.fillStyle(color, 0.6);
        graphics.fillCircle(0, 0, 4);
        graphics.lineStyle(2, color, 0.8);
        graphics.strokeCircle(0, 0, 6);
    }
}
